import { Briefcase, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fiscalMonths, SystemFeatureFlags } from "./types";
import { useLanguage } from "@/lib/i18n";

interface LeaveTabProps {
  leaveDashboard: any;
  leaveDraft: {
    leaveType: "vacation" | "sick" | "business";
    startDate: string;
    endDate: string;
    reason: string;
    handoverTo: string;
    handoverDetails: string;
  };
  setLeaveDraft: React.Dispatch<
    React.SetStateAction<{
      leaveType: "vacation" | "sick" | "business";
      startDate: string;
      endDate: string;
      reason: string;
      handoverTo: string;
      handoverDetails: string;
    }>
  >;
  createLeave: any;
  cancelLeave: any;
  idToken: string | null;
  featureFlags?: SystemFeatureFlags;
}

export function LeaveTab({
  leaveDashboard,
  leaveDraft,
  setLeaveDraft,
  createLeave,
  cancelLeave,
  idToken,
  featureFlags,
}: LeaveTabProps) {
  const { t, language } = useLanguage();
  const tx = (th: string, my: string) => (language === "my" ? my : th);
  if (featureFlags?.enableLeaveSystem === false) {
    return (
      <Card className="glass-panel rounded-3xl border-rose-200 bg-rose-50/90 p-6 shadow-md">
        <div className="flex items-center gap-3 text-rose-800">
          <ShieldAlert size={28} className="shrink-0" />
          <div>
            <h3 className="text-base font-black">{tx("ระบบยื่นคำขอลาปิดใช้งานชั่วคราว", "ခွင့်တောင်းခံမှုစနစ်ကို ယာယီပိတ်ထားသည်")}</h3>
            <p className="text-xs font-semibold text-rose-700 mt-0.5">
              {tx("ผู้ดูแลระบบปิดฟีเจอร์นี้ชั่วคราว กรุณาติดต่อ HR", "စီမံခန့်ခွဲသူက ဤလုပ်ဆောင်ချက်ကို ယာယီပိတ်ထားသည်။ HR ကို ဆက်သွယ်ပါ။")}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  if (leaveDashboard.isLoading) {
    return (
      <Card className="glass-panel rounded-3xl p-8 text-center">
        <RefreshCw className="mx-auto mb-3 animate-spin text-teal-700" size={28} />
        <p className="font-black text-slate-800">{tx("กำลังโหลดข้อมูลวันลา…", "ခွင့်ရက်အချက်အလက် ရယူနေသည်…")}</p>
      </Card>
    );
  }
  if (leaveDashboard.error) {
    return (
      <Card className="glass-panel rounded-3xl border-rose-200 bg-rose-50/90 p-6 text-center">
        <p className="font-black text-rose-800">{tx("โหลดข้อมูลวันลาไม่สำเร็จ", "ခွင့်ရက်အချက်အလက် ရယူ၍မရပါ")}</p>
        <p className="mt-1 text-xs text-rose-600">{leaveDashboard.error.message}</p>
      </Card>
    );
  }
  if (!leaveDashboard.data) return null;

  return (
    <Card className="glass-panel rounded-3xl border-2 border-slate-300 shadow-xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="text-teal-700" />
          {t.leaveHeading}
        </CardTitle>
        <CardDescription>
          {t.leaveSubheading} · {tx("ปีงบประมาณ", "ဘဏ္ဍာနှစ်")} {leaveDashboard.data.period.fiscalYear}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              [t.leaveTypeVacation, "vacation"],
              [t.leaveTypeSick, "sick"],
              [t.leaveTypeBusiness, "business"],
            ] as const
          ).map(([label, key]) => (
            <div key={key} className="rounded-2xl border-2 border-slate-300 bg-white p-3">
              <p className="text-xs font-bold text-slate-500">{label}</p>
              <p className="text-2xl font-black text-teal-800">
                {leaveDashboard.data.remaining[key]} {tx("วัน", "ရက်")}
              </p>
              <p className="text-[10px] text-slate-500">
                {tx("ใช้แล้ว", "အသုံးပြုပြီး")} {leaveDashboard.data.used[key]} {tx("วัน", "ရက်")}
              </p>
            </div>
          ))}
        </div>

        <div className="overflow-x-auto">
          <h3 className="font-black mb-2">{tx("สรุปสถิติประจำปีงบประมาณ", "ဘဏ္ဍာနှစ် စာရင်းအကျဉ်း")}</h3>
          <table className="w-full min-w-[680px] text-xs border-separate border-spacing-1">
            <thead>
              <tr>
                <th className="text-left p-2">{tx("เดือน", "လ")}</th>
                {fiscalMonths(leaveDashboard.data.period.fiscalYear).map((m) => (
                  <th key={m.key} className="rounded-lg bg-slate-800 p-2 text-white">
                    {m.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="font-bold p-2">{tx("จำนวนครั้ง", "အကြိမ်ရေ")}</td>
                {fiscalMonths(leaveDashboard.data.period.fiscalYear).map((m) => (
                  <td
                    key={m.key}
                    className="rounded-lg border border-slate-300 bg-white p-2 text-center font-bold"
                  >
                    {
                      leaveDashboard.data.requests.filter(
                        (r: any) => r.status === "approved" && r.startDate.startsWith(m.key)
                      ).length
                    }
                  </td>
                ))}
              </tr>
              <tr>
                <td className="font-bold p-2">{tx("จำนวนวัน", "ရက်အရေအတွက်")}</td>
                {fiscalMonths(leaveDashboard.data.period.fiscalYear).map((m) => (
                  <td
                    key={m.key}
                    className="rounded-lg border border-slate-300 bg-white p-2 text-center font-bold text-teal-800"
                  >
                    {leaveDashboard.data.requests
                      .filter((r: any) => r.status === "approved" && r.startDate.startsWith(m.key))
                      .reduce((s: number, r: any) => s + r.days, 0)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl border-2 border-slate-300 bg-slate-50 p-4">
          <h3 className="font-black mb-3">{tx("เพิ่มข้อมูลการลาและรับมอบงาน", "ခွင့်နှင့် အလုပ်လွှဲပြောင်းမှု ထည့်ပါ")}</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              className="rounded-xl border-2 border-slate-400 bg-white p-3"
              value={leaveDraft.leaveType}
              onChange={(e) =>
                setLeaveDraft({ ...leaveDraft, leaveType: e.target.value as any })
              }
            >
              <option value="vacation">{t.leaveTypeVacation}</option>
              <option value="sick">{t.leaveTypeSick}</option>
              <option value="business">{t.leaveTypeBusiness}</option>
            </select>
            <input
              className="rounded-xl border-2 border-slate-400 p-3"
              placeholder={tx("ผู้รับมอบงาน", "အလုပ်လက်ခံသူ")}
              value={leaveDraft.handoverTo}
              onChange={(e) => setLeaveDraft({ ...leaveDraft, handoverTo: e.target.value })}
            />
            <input
              type="date"
              className="rounded-xl border-2 border-slate-400 p-3"
              value={leaveDraft.startDate}
              onChange={(e) => setLeaveDraft({ ...leaveDraft, startDate: e.target.value })}
            />
            <input
              type="date"
              className="rounded-xl border-2 border-slate-400 p-3"
              value={leaveDraft.endDate}
              onChange={(e) => setLeaveDraft({ ...leaveDraft, endDate: e.target.value })}
            />
            <textarea
              className="rounded-xl border-2 border-slate-400 p-3"
              placeholder={t.leaveReason}
              value={leaveDraft.reason}
              onChange={(e) => setLeaveDraft({ ...leaveDraft, reason: e.target.value })}
            />
            <textarea
              className="rounded-xl border-2 border-slate-400 p-3"
              placeholder={tx("รายละเอียดงานที่ส่งมอบ", "လွှဲပြောင်းပေးသည့် အလုပ်အသေးစိတ်")}
              value={leaveDraft.handoverDetails}
              onChange={(e) =>
                setLeaveDraft({ ...leaveDraft, handoverDetails: e.target.value })
              }
            />
          </div>
          <Button
            className="mt-3 bg-teal-800"
            disabled={
              createLeave.isPending ||
              !leaveDraft.startDate ||
              !leaveDraft.endDate ||
              !leaveDraft.reason ||
              !leaveDraft.handoverTo ||
              !leaveDraft.handoverDetails
            }
            onClick={() => idToken && createLeave.mutate({ idToken, ...leaveDraft })}
          >
            {t.leaveSubmitBtn}
          </Button>
        </div>

        <div>
          <h3 className="font-black mb-2">{tx("ปฏิทินการลา", "ခွင့်ပြက္ခဒိန်")}</h3>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
            {fiscalMonths(leaveDashboard.data.period.fiscalYear).map((m) => (
              <div key={m.key} className="min-h-24 rounded-xl border-2 border-slate-300 bg-white p-2">
                <p className="font-black text-slate-800">{m.label}</p>
                {leaveDashboard.data.requests
                  .filter(
                    (r: any) => r.startDate.startsWith(m.key) && r.status !== "cancelled"
                  )
                  .map((r: any) => (
                    <div
                      key={r.id}
                      className={`mt-1 rounded-md px-1.5 py-1 text-[10px] font-bold ${
                        r.status === "approved"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                    >
                      {r.startDate.slice(8)}–{r.endDate.slice(8)} · {r.days} {tx("วัน", "ရက်")}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-black mb-2">{tx("ประวัติคำขอ", "တောင်းဆိုမှုမှတ်တမ်း")}</h3>
          <div className="space-y-2">
            {leaveDashboard.data.requests.map((r: any) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-xl border-2 border-slate-300 bg-white p-3"
              >
                <div>
                  <p className="font-bold">
                    {r.startDate} – {r.endDate} · {r.days} {tx("วัน", "ရက်")}
                  </p>
                  <p className="text-xs text-slate-500">
                    {r.reason} · {tx("มอบงานให้", "အလုပ်လွှဲပေးသူ")} {r.handoverTo}
                  </p>
                </div>
                <div className="text-right">
                  <Badge>{r.status}</Badge>
                  {r.status === "pending" && (
                    <button
                      className="block mt-1 text-xs font-bold text-rose-700"
                      onClick={() => idToken && cancelLeave.mutate({ idToken, id: r.id })}
                    >
                      {tx("ยกเลิก", "ပယ်ဖျက်")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
